import { useTranslation } from 'react-i18next';
import {
  hasBasis,
  signColorClass,
  type OpenConstructionCostView,
  type OpenCostBasisMode,
} from '@/utils/approval/openConstructionCost';
import {
  BasisToggleButton,
  BrandIcon,
  JudgeArrow,
  Scenarios,
  StatusTag,
} from './parts';

type OpenConstructionCostReportProps = {
  view: OpenConstructionCostView;
  /** null 이면 기준별 데이터 없음 — roi 기본값으로 그림 */
  basisMode: OpenCostBasisMode | null;
  onToggleBasis?: () => void;
};

/**
 * 레거시 #open_construction_cost_content 안쪽 마크업 그대로(renderConstructionCost·applyBasis 결과).
 * 클래스·id 를 유지해야 저장 content 를 레거시 조회 화면이 같은 CSS·토글 스크립트로 그림
 */
const OpenConstructionCostReport = ({
  view,
  basisMode,
  onToggleBasis,
}: OpenConstructionCostReportProps) => {
  const { t } = useTranslation();
  const { project: p, salesProfit: s, compare: c, roi: r } = view;
  const basis = basisMode ? view.basis[basisMode] : null;
  const active = hasBasis(basis) ? basis : null;
  const canToggle =
    hasBasis(view.basis.operating) && hasBasis(view.basis.direct);

  const roiStatus = active?.status || r.status;
  const rateStatus = active ? active.rateStatus : r.rateStatus;
  const opinionHTML = active?.opinionHTML || view.overallOpinionHTML || '-';
  const scenarios = active ? active.scenarios : r.scenarios;

  // 인테리어 보고서가 정해진 한국어 문구로 보내는 값 — 표시만 번역, 판정은 원래 값으로
  const caption = c.caption ? t(c.caption) : '';
  const trendText = (text: string) =>
    text.replace(/\s(증가|감소)$/, (_, word: string) => ` ${t(word)}`);
  const tolerancePct = c.tolerance.match(/^편차\s*([+-]?[\d.]+%)\s*이내$/)?.[1];
  const toleranceText = tolerancePct
    ? t('허용편차 {{pct}} 이내', { pct: tolerancePct })
    : t('허용 {{tolerance}}', {
        tolerance: c.tolerance,
        interpolation: { escapeValue: false },
      });

  // 포탈 보고서가 보낸 설명 줄(note)이 있으면 그대로, 없을 때만 편차·허용기준으로 조립
  const designNoteHtml =
    !c.note && c.deviationPercent
      ? `${
          caption || t('관리기준 평단가 대비 편차')
        } <strong class="${signColorClass(c.deviationPercent, true)}">${
          c.deviationPercent
        }</strong>${c.tolerance ? ` · ${toleranceText}` : ''}`
      : '';

  return (
    <>
      <div className="detail-section">
        <div className="section-title">{t('프로젝트 개요')}</div>
        <div className="info-grid">
          <div className="info-item">
            <div className="info-label">{t('브랜드')}</div>
            <div className="info-value brand-info">
              {p.brand ? (
                <BrandIcon brand={p.brand} />
              ) : (
                <div className="brand-logo mlb" style={{ display: 'none' }} />
              )}
              <span className="value_brand">{p.brand || '-'}</span>
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">{t('프로젝트명')}</div>
            <div className="info-value" id="value_project_name">
              {p.projectName || '-'}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">{t('채널')}</div>
            <div className="info-value" id="value_chanel">
              {p.channel || '-'}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">{t('면적')}</div>
            <div className="info-value" id="value_area">
              {p.area || '-'}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">{t('공사기간')}</div>
            <div className="info-value" id="value_construction_period">
              {p.period || '-'}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">{t('오픈일')}</div>
            <div className="info-value" id="value_date_open">
              {p.openDate || '-'}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">{t('예상 총공사비')}</div>
            <div className="info-value" id="value_expected_cost">
              {p.expectedCost || '-'}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">{t('공사유형')}</div>
            <div className="info-value content" id="value_construction_type">
              {p.constructionType || '-'}
            </div>
          </div>
        </div>
      </div>

      <div className="detail-section mt-40">
        <div className="section-title">{t('매출 · 손익 요약')}</div>
        <div className="table-container mt-10">
          <table className="sales-table table">
            <colgroup>
              <col style={{ width: '50%' }} />
              <col style={{ width: '50%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>{t('내용', { context: 'item' })}</th>
                <th className="text-right">{t('금액')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  {t('금번 매출목표')} <span className="th-note">(V+)</span>
                </td>
                <td className="text-right">
                  <strong id="value_sales_target">{s.target || '-'}</strong>
                  <span className="cell-sub" id="value_sales_target_monthly">
                    {s.targetMonthly}
                  </span>
                </td>
              </tr>
              <tr>
                <td>
                  <span id="label_sales_last_year">
                    {s.lastYearLabel || t('전년 매출실적')}
                  </span>{' '}
                  <span className="th-note">(V+)</span>
                </td>
                <td className="text-right">
                  <strong id="value_sales_last_year">
                    {s.lastYear || '-'}
                  </strong>
                  <span className="cell-sub" id="value_sales_last_year_monthly">
                    {s.lastYearMonthly}
                  </span>
                </td>
              </tr>
              <tr>
                <td>{t('본 매장 월 영업이익')}</td>
                <td className="text-right">
                  <strong id="value_operating_profit">
                    {s.operatingProfit || '-'}
                  </strong>
                  <span className="cell-sub" id="value_operating_profit_rate">
                    {s.operatingProfitRate}
                  </span>
                </td>
              </tr>
              <tr>
                <td>{t('본 매장 월 직접이익')}</td>
                <td className="text-right">
                  <strong id="value_direct_profit">
                    {s.directProfit || '-'}
                  </strong>
                  <span className="cell-sub" id="value_direct_profit_rate">
                    {s.directProfitRate}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="cc-section overall-opinion">
        <div className="opinion-head">
          <span className="ai-pill">{t('AI 투자 적정성 종합의견')}</span>
          <BasisToggleButton
            basisMode={basisMode}
            hidden={!canToggle}
            onClick={onToggleBasis}
          />
        </div>
        <p
          className="opinion-text"
          id="value_overall_opinion"
          dangerouslySetInnerHTML={{ __html: opinionHTML }}
        />
      </div>

      <div className="cc-section feasibility-cards">
        <div className="feasibility-card is-blue">
          <div className="card-head">
            <div className="card-title">
              {t('비교군 대비 및 설계등급 적정성')}
            </div>
            <StatusTag
              id="value_compare_status"
              status={c.status}
              variant={c.statusVariant}
            />
          </div>
          <div className="card-divider" />
          <div className="card-body">
            <div className="card-caption" id="value_compare_caption">
              {caption || '-'}
            </div>
            <div className="card-metric val-blue" id="value_compare_deviation">
              {c.deviationText ? trendText(c.deviationText) : '-'}
            </div>
            <table className="mini-table">
              <tbody>
                <tr>
                  <th>{t('비교군 평단가')}</th>
                  <td id="value_compare_benchmark">
                    {c.benchmarkPrice || '-'}
                  </td>
                </tr>
                <tr>
                  <th>{t('본 매장 평단가')}</th>
                  <td id="value_compare_store">{c.storePrice || '-'}</td>
                </tr>
                <tr>
                  <th>{t('총 공사비')}</th>
                  <td id="value_compare_total">{c.totalCost || '-'}</td>
                </tr>
              </tbody>
            </table>
            <div className="card-note">
              <span className="note-chip">
                <span id="value_design_grade">
                  {c.tag || c.designGrade || '-'}
                </span>
                <span
                  className="chip-divider"
                  style={c.designStatus ? undefined : { display: 'none' }}
                />
                <strong
                  id="value_design_status"
                  className={
                    c.designStatus.includes('초과')
                      ? 'val-caution'
                      : 'val-success'
                  }
                  style={c.designStatus ? undefined : { display: 'none' }}
                >
                  {c.designStatus && t(c.designStatus, { context: 'verdict' })}
                </strong>
              </span>
              {c.note ? (
                <p className="note-desc mb-0" id="value_design_note">
                  {c.note}
                </p>
              ) : (
                <p
                  className="note-desc mb-0"
                  id="value_design_note"
                  dangerouslySetInnerHTML={{ __html: designNoteHtml }}
                />
              )}
              <p className="note-desc mb-0" id="value_design_reason">
                {c.reasonLine || c.reason}
              </p>
            </div>
          </div>
        </div>

        <div className="feasibility-card is-violet">
          <div className="card-head">
            <div className="card-title">{t('투자회수 적정성')}</div>
            <StatusTag
              id="value_roi_status"
              status={roiStatus}
              label={t('회수 속도')}
            />
            <StatusTag
              id="value_roi_rate_status"
              status={rateStatus}
              label={t('이익률')}
              hidden={!rateStatus}
            />
          </div>
          <div className="card-divider" />
          <div className="card-body">
            <div className="card-caption">{t('매출 변동 시나리오 분석')}</div>
            <div className="metric-row">
              <div className="card-metric val-violet" id="value_roi_period">
                {(active ? active.period : r.period) || '-'}
              </div>
              <StatusTag
                id="value_roi_period_grade"
                status={roiStatus}
                hidden={!roiStatus}
              />
            </div>
            <div className="card-subcaption" id="value_roi_subcaption">
              {active?.subcaption ?? ''}
            </div>
            <table className="mini-table">
              <tbody>
                <tr>
                  <th id="label_roi_profit">
                    {active?.profitLabel ?? t('월 직접이익')}
                  </th>
                  <td id="value_roi_profit">
                    {(active ? active.monthlyProfit : r.monthlyProfit) || '-'}
                  </td>
                </tr>
                <tr>
                  <th>{t('계획 운영 기간')}</th>
                  <td id="value_roi_planned">{r.plannedPeriod || '-'}</td>
                </tr>
                <tr>
                  <th>{t('이익률')}</th>
                  <td>
                    <span id="value_roi_rate">
                      {(active ? active.profitRate : r.profitRate) || '-'}
                    </span>{' '}
                    <StatusTag
                      id="value_roi_rate_grade"
                      status={rateStatus}
                      hidden={!rateStatus}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
            <Scenarios scenarios={scenarios} />
          </div>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="cc-section special-notes">
        <div>
          <span className="ai-pill">{t('AI 공사내용 특이사항 분석')}</span>
        </div>
        <div
          className="notes-body"
          id="value_special_notes"
          dangerouslySetInnerHTML={{ __html: view.specialNotesHTML || '-' }}
        />
      </div>

      <div className="summary mt-40">
        <div className="section-title">{t('공사비 요약')}</div>
        <p className="info-text text-muted mt-5">
          {t(
            '공종별 공사금액·평단가 · 분류 2(영역구분) 기준 · 비교군(관리기준 또는 유사매장 평균) 평단가 대비',
          )}
        </p>
        <div className="table-container mt-10">
          <table className="budget-table table table-fixed">
            <colgroup>
              <col style={{ width: 203 }} />
              <col style={{ width: 203 }} />
              <col style={{ width: 203 }} />
              <col style={{ width: 203 }} />
              <col style={{ width: 132 }} />
              <col style={{ width: 80 }} />
              <col style={{ width: 203 }} />
            </colgroup>
            <thead>
              <tr>
                <th>{t('공종')}</th>
                <th className="text-right">{t('공사금액')}</th>
                <th className="text-right">{t('본 매장 평단가')}</th>
                <th className="text-right" id="value_benchmark_header">
                  {view.benchmarkType ? t(view.benchmarkType) : t('평단가')}
                </th>
                <th className="text-center">{t('비교군 대비')}</th>
                <th className="text-center">{t('판정')}</th>
                <th>{t('공종 내용 분석')}</th>
              </tr>
            </thead>
            <tbody>
              {view.budgetSummary.length ? (
                view.budgetSummary.map((row, i) => {
                  const dev = (row.deviation || '').trim();
                  const hasDev = !!dev && /\d/.test(dev);
                  // '-' 로 시작하면 감소(초록), 아니면 증가(빨강)
                  const isOver = dev.charAt(0) !== '-';
                  return (
                    <tr key={`${row.type}-${i}`}>
                      <td>{row.type}</td>
                      <td className="text-right">{row.amount}</td>
                      <td className="text-right">{row.storePrice}</td>
                      <td className="text-right">
                        {row.benchmarkPrice || '-'}
                      </td>
                      <td className="text-center">
                        {hasDev ? (
                          <span
                            className={isOver ? 'val-caution' : 'val-success'}
                          >
                            {dev}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="text-center">
                        {hasDev ? (
                          <span
                            className={`judge-tag ${
                              isOver ? 'is-caution' : 'is-success'
                            }`}
                          >
                            <JudgeArrow up={isOver} />
                            {isOver ? t('증가') : t('감소')}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>{row.note}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default OpenConstructionCostReport;
